import React from 'react';

import type {
  DocumentationFeedback,
  PossibleCause,
} from './TSFADAnalysis.shared';

type StylesMap = Record<string, any>;

type SuggestCauseOptions = {
  suggestionText?: string;
};

type OpenFeedbackModalArgs = {
  cause?: PossibleCause | null;
  defaults?: DocumentationFeedback;
  relatedDescription?: string | null;
};

type TSFADSuggestionModalProps = {
  open: boolean;
  styles: StylesMap;
  suggestion: string;
  setSuggestion: React.Dispatch<React.SetStateAction<string>>;
  suggestionLoading: boolean;
  setSuggestionLoading: React.Dispatch<React.SetStateAction<boolean>>;
  suggestionResult: any;
  setSuggestionResult: React.Dispatch<React.SetStateAction<any>>;
  setShowSuggestionModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleSuggestCause: (options?: SuggestCauseOptions) => Promise<any>;
  handleAddUnverifiedCause: () => Promise<any>;
  openFeedbackModal: (args: OpenFeedbackModalArgs) => void;
};

export const TSFADSuggestionModal: React.FC<TSFADSuggestionModalProps> = ({
  open,
  styles,
  suggestion,
  setSuggestion,
  suggestionLoading,
  setSuggestionLoading,
  suggestionResult,
  setSuggestionResult,
  setShowSuggestionModal,
  handleSuggestCause,
  handleAddUnverifiedCause,
  openFeedbackModal,
}) => {
  if (!open) return null;

  const resultHeading = suggestionResult?.outcome === 'ADDED_UNVERIFIED'
    ? 'Added as Unverified Cause'
    : suggestionResult?.accepted
    ? 'Accepted and Enriched'
    : suggestionResult?.outcome === 'DUPLICATE'
    ? 'Already Covered'
    : suggestionResult?.outcome === 'KNOWLEDGE_GAP'
    ? 'Knowledge Gap'
    : suggestionResult?.outcome === 'ERROR'
    ? 'Evaluation Failed'
    : 'Not Added';
  const resultStyle =
    suggestionResult?.outcome === 'DUPLICATE'
      ? styles.acceptedResult
      : suggestionResult?.accepted
      ? styles.attentionResult
      : styles.rejectedResult;

  return (
    <div style={styles.modal}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Suggest a Possible Cause</h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Is there something missing from the analysis? Suggest a cause that should be tested.
          </p>
        </div>

        {!suggestionResult ? (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Your Suggestion:
            </label>
            <textarea
              style={styles.textarea}
              placeholder="E.g., 'Lamp failure', 'Contaminated reagent', 'Clogged waste line', etc."
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
              disabled={suggestionLoading}
            />
          </div>
        ) : (
          <div style={{ marginBottom: '1rem' }}>
            <div
              style={{
                color: '#6b7280',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase'
              }}
            >
              Suggested Cause
            </div>
            <div
              style={{
                marginTop: '0.35rem',
                padding: '0.65rem 0.75rem',
                backgroundColor: '#f8fafc',
                border: '1px solid #dbe3e8',
                borderRadius: '0.375rem',
                color: '#173f42'
              }}
            >
              {suggestionResult.originalSuggestion || suggestion}
            </div>
          </div>
        )}

        {suggestionResult && (
          <div
            style={{
              ...styles.suggestionResult,
              ...resultStyle
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <strong>{resultHeading}</strong>
            </div>

            <p style={{ margin: '0.5rem 0 0 0' }}>
              {suggestionResult.reasoningSummary || suggestionResult.reasoning}
            </p>

            {suggestionResult.accepted && suggestionResult.outcome !== 'ADDED_UNVERIFIED' && (
              <p style={{ margin: '0.75rem 0 0', fontWeight: 600 }}>
                Added to Possible Causes and an action plan has been created.
              </p>
            )}

            {!suggestionResult.accepted &&
              suggestionResult.rejectionDetails?.explanation &&
              suggestionResult.rejectionDetails.explanation !==
                (suggestionResult.reasoningSummary || suggestionResult.reasoning) && (
                <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#4b5563' }}>
                  {suggestionResult.rejectionDetails.explanation}
                </p>
              )}

            {!suggestionResult.accepted && suggestionResult.rejectionDetails && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontSize: '0.8rem', color: 'inherit', fontWeight: 600 }}>
                  Reason: {suggestionResult.rejectionDetails.primaryReason.replace(/_/g, ' ')}
                </div>
                {suggestionResult.outcome !== 'DUPLICATE' &&
                  suggestionResult.outcome !== 'ERROR' && (
                    <button
                      style={{
                        marginTop: '0.75rem',
                        padding: '0.5rem 1rem',
                        backgroundColor: '#92400e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                      disabled={suggestionLoading}
                      onClick={() => {
                        const suggestionText = suggestion;
                        const feedbackDefaults: DocumentationFeedback = {
                          feedbackType: 'MISSING_CAUSE',
                          severity: 'HIGH',
                          title: `Missing cause: ${suggestionText}`,
                          description: `The suggestion "${suggestionText}" was rejected but appears to be valid. ${suggestionResult.rejectionDetails.explanation}`,
                          impact: 'Technician knowledge not captured in documentation'
                        };

                        setShowSuggestionModal(false);
                        setSuggestion('');
                        setSuggestionResult(null);
                        openFeedbackModal({
                          cause: null,
                          defaults: feedbackDefaults,
                          relatedDescription: suggestionText
                        });
                      }}
                    >
                      Report Documentation Gap
                    </button>
                )}
                {suggestionResult.outcome === 'KNOWLEDGE_GAP' &&
                  suggestionResult.unverifiedAddToken && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: '#7f1d1d' }}>
                        You can still add this as an unverified technician-suggested cause. It will
                        be added without an action plan because supporting service information is
                        not available.
                      </p>
                      <button
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#0f4c4f',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: suggestionLoading ? 'not-allowed' : 'pointer',
                          fontSize: '0.875rem'
                        }}
                        disabled={suggestionLoading}
                        onClick={() => handleAddUnverifiedCause()}
                      >
                        {suggestionLoading ? 'Adding...' : 'Add as Unverified Cause'}
                      </button>
                    </div>
                  )}
              </div>
            )}
          </div>
        )}

        <div style={suggestionResult ? styles.buttonRow : styles.buttonGroup}>
          {!suggestionResult && (
            <>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={() => handleSuggestCause()}
                disabled={!suggestion.trim() || suggestionLoading}
              >
                {suggestionLoading ? 'Evaluating...' : 'Submit Suggestion'}
              </button>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                disabled={suggestionLoading}
                onClick={() => {
                  setShowSuggestionModal(false);
                  setSuggestion('');
                  setSuggestionResult(null);
                  setSuggestionLoading(false);
                }}
              >
                Cancel
              </button>
            </>
          )}

          {suggestionResult && suggestionResult.accepted && (
            <>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={() => {
                  setSuggestion('');
                  setSuggestionResult(null);
                  setSuggestionLoading(false);
                }}
              >
                Submit Another
              </button>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => {
                  setShowSuggestionModal(false);
                  setSuggestion('');
                  setSuggestionResult(null);
                  setSuggestionLoading(false);
                }}
              >
                Done
              </button>
            </>
          )}

          {suggestionResult && !suggestionResult.accepted && (
            <>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={() => {
                  setSuggestion('');
                  setSuggestionResult(null);
                }}
                disabled={suggestionLoading}
              >
                Submit New Suggestion
              </button>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => {
                  setShowSuggestionModal(false);
                  setSuggestion('');
                  setSuggestionResult(null);
                }}
                disabled={suggestionLoading}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
